import React, { useState, useContext } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { notify } from '../../helpers/notifications';
import slugify from '../../helpers/slugify';
import Icon from '../common/Icon';
import TimeStamp from '../common/TimeStamp';
import { MarkedEditor } from '../common/Marked';
import Row from '../common/Row';
import Errors from '../common/Errors';
import { handleEditForm, destroyForm } from './constants/handleForm';
import FormMetaEntries from './form-meta-entries/FormMetaEntries';
import { FormContext } from '../../hooks/Contexts';
import { updateFormMetadata } from './form-meta-entries/handleFormMetaEntries';
import { useNavigate } from 'react-router-dom';

const FormEdit = ({ toggleEdit, informant }) => {
  const { form, setForm, setFormMetaEntries } = useContext(FormContext);
  const [showMeta, setShowMeta] = useState(true);
  const [dirtyMetas, setDirtyMetas] = useState([]);
  const [metaErrors, setMetaErrors] = useState([]);
  const navigate = useNavigate();
  const {
    container_id,
    created_at,
    id,
    submissions_count,
    updated_at,
    fieldsets,
    form_meta_entries,
    ...editableFields
  } = form;

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: { ...editableFields },
    criteriaMode: 'all',
    shouldUnregister: true,
  });

  const { dirtyFields } = useFormState({ control });
  const disableActiveRow = watch('archived');

  const onSubmit = async (formData) => {
    let metasResolved = dirtyMetas.length == 0 && metaErrors.length == 0;
    if (!metasResolved) {
      let promises = [];
      dirtyMetas.forEach((formMeta) => {
        promises.push(updateFormMetadata(formMeta, formMeta.id));
      });
      metasResolved = await Promise.all(promises).then((results) => {
        let resultErrors = [];
        results.forEach((res) => {
          if (res.result?.ok) {
            setFormMetaEntries((entries) => {
              let updated = entries.find((me) => me.id === res.id);
              entries[entries.indexOf(updated)] = res.result.data;
              return [...entries];
            });
          } else if (res.error) {
            console.error(res);
            resultErrors.push(res);
          }
        });
        setMetaErrors(resultErrors);
        return resultErrors.length == 0;
      });
      if (metasResolved) {
        notify.success('Form Metadata Saved');
      } else {
        notify.failure('Form Metadata Error');
      }
    }

    if (metasResolved) {
      if (Object.keys(dirtyFields).length) {
        let editedForm = await handleEditForm(formData, id);
        if (editedForm.ok) {
          setForm(editedForm.data);
          toggleEdit();
        } else if (editedForm.errors) {
          Object.entries(editedForm.errors).forEach(([key, message]) =>
            setError(key, { types: { [key]: message } })
          );
        }
      } else {
        toggleEdit();
      }
    }
  };

  const handleDestroyForm = async (e) => {
    e.preventDefault();
    if (form.id && form.container_id)
      await destroyForm(form.id, (response) => {
        if (response.ok) navigate(`/manage/containers/${form.container_id}`);
      });
  };

  return (
    <div className='details lower-control-space'>
      <div className='controls'>
        <Icon icon='xmark' onClick={(e) => toggleEdit()} title='Cancel Edit' />
      </div>
      <form
        onSubmit={handleSubmit(onSubmit)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && e.target.type != 'textarea')
            e.preventDefault();
        }}
      >
        <div className='controls lower'>
          <button className='danger' onClick={handleDestroyForm}>
            Delete
            <Icon icon='trash' />
          </button>
          <button type='submit'>
            Save Form
            <Icon icon='save' />
          </button>
          <button
            className='cancel blue'
            onClick={(e) => {
              e.preventDefault();
              toggleEdit();
            }}
          >
            Cancel
            <Icon icon='xmark' />
          </button>
        </div>
        <div style={{ marginTop: '20px' }} />
        <Row name='Display Name:'>
          <input {...register('display_name')} />
        </Row>
        <Row name='Name:'>
          <input
            {...register('name', {
              required: 'Required',
              onChange: (e) =>
                setValue('slug', slugify(e.target.value), {
                  shouldDirty: true,
                }),
            })}
          />
          <ErrorMessage
            errors={errors}
            name='name'
            render={({ messages }) => <Errors messages={messages} />}
          />
        </Row>
        <Row name='Permalink:'>
          <input
            {...register('slug', {
              required: 'Required',
              onBlur: (e) =>
                setValue('slug', slugify(e.target.value), {
                  shouldDirty: true,
                }),
            })}
          />
          <ErrorMessage
            errors={errors}
            name='slug'
            render={({ messages }) => <Errors messages={messages} />}
          />
        </Row>
        <Row name='Splash Page Text: (Optional)'>
          <MarkedEditor
            register={{ ...register('splash') }}
            text={form.splash}
            getValues={() => getValues('splash')}
          />
        </Row>
        <Row name='Inactive Text: (Optional)'>
          <MarkedEditor
            register={{ ...register('inactive_text') }}
            text={form.inactive_text}
            getValues={() => getValues('inactive_text')}
          />
        </Row>
        <Row name='Description:'>
          <MarkedEditor
            register={{ ...register('description') }}
            text={form.description}
            getValues={() => getValues('description')}
          />
        </Row>
        <Row name='Completion Text:'>
          <MarkedEditor
            register={{ ...register('completion_text') }}
            text={form.completion_text}
            getValues={() => getValues('completion_text')}
          />
        </Row>
        <Row name='Global Quota:'>
          {informant ? (
            form.global_quota
          ) : (
            <input
              type='number'
              min={0}
              {...register('global_quota', {
                setValueAs: (v) => (v == 0 ? null : v),
              })}
            />
          )}
        </Row>
        <Row name='Active:' className={disableActiveRow ? 'row-disabled' : ''}>
          {informant ? (
            <>
              {form.active ? 'Yes ' : 'No '} - Managed by Field:{' '}
              <a href={`/manage/fields/${informant.id}`}>{informant.name}</a>
            </>
          ) : (
            <>
              <input
                type='checkbox'
                id='form-active'
                {...register('active', {
                  disabled: disableActiveRow,
                })}
              />
              <label htmlFor='form-active'></label>
            </>
          )}
        </Row>
        <Row name='Archived:'>
          <input
            type='checkbox'
            id='form-archived'
            {...register('archived', {
              onChange: (e) => {
                if (e.target.checked) setValue('active', false);
              },
            })}
          />
          <label htmlFor='form-archived'></label>
        </Row>

        <Row
          name='Is Parent:'
          className={watch('submission_validate') ? 'row-disabled' : false}
        >
          <input
            type='checkbox'
            id='is-parent'
            {...register('is_parent', {
              disabled: watch('submission_validate'),
            })}
          />
          <label htmlFor='is-parent'></label>
          <ErrorMessage
            errors={errors}
            name='is_parent'
            render={({ messages }) => <Errors messages={messages} />}
          />
        </Row>

        <Row
          name='Is Child:'
          className={
            watch('is_parent') || watch('course_validate')
              ? 'row-disabled'
              : false
          }
        >
          <input
            type='checkbox'
            id='sub-validate'
            {...register('submission_validate', {
              disabled:
                form.is_parent ||
                watch('is_parent') ||
                watch('course_validate'),
            })}
          />
          <label htmlFor='sub-validate'></label>
        </Row>

        {watch('submission_validate') && (
          <>
            <Row name='Single Use:'>
              <input
                type='checkbox'
                id='single-use'
                {...register('single_use_only')}
              />
              <label htmlFor='single-use'></label>
            </Row>
            <Row name='Parent Form ID:'>
              <input
                type='text'
                id='parent-form-id'
                {...register('linked_parent_form_id', {
                  required: 'Required',
                  disabled: !watch('submission_validate'),
                })}
              />
              <ErrorMessage
                errors={errors}
                name='linked_parent_form_id'
                render={({ messages }) => <Errors messages={messages} />}
              />
            </Row>
          </>
        )}
        <Row
          name='Course Validated:'
          className={watch('submission_validate') ? 'row-disabled' : ''}
        >
          <input
            type='checkbox'
            id='form-course_validate'
            {...register('course_validate', {
              disabled: watch('submission_validate'),
            })}
          />
          <label htmlFor='form-course_validate'></label>
        </Row>

        {watch('course_validate') && (
          <Row name='SSLP Course ID:'>
            <input {...register('course_id')} />
          </Row>
        )}

        {(watch('course_validate') || watch('submission_validate')) && (
          <Row name='Form Password (Optional):'>
            <input {...register('password')} />
          </Row>
        )}
        <TimeStamp title='Created' value={created_at} />
        <TimeStamp title='Updated' value={updated_at} />
      </form>
      <FormMetaEntries
        show={showMeta}
        toggle={() => setShowMeta(!showMeta)}
        editor={{ dirtyMetas, setDirtyMetas, metaErrors }}
      />
      <hr />
    </div>
  );
};

export default FormEdit;
